import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import { NavigationBar, NavigationBarProps } from '@oneaudi/unified-web-components';
import { Context } from '../../../../context/Context';
import { useTrackingManager } from '../../../../utils/useTrackingManager';
import { DesktopMenuContainer, DesktopMenuWrapper } from '../../SubPageNavigationStyles';
import { useI18n } from '../../../../i18n';
import { SUBMENUID, TRIGGERID } from '../../../../ContentType';
import { attachHorizontalNavigation } from '../../../../utils/a11y';
import { useBreakpoint } from '../../../../utils/useBreakpoint';

const DesktopMenu: React.FC = () => {
  const {
    stickymode,
    hasCta,
    content: { links },
    submenuDesktopRef,
    showSubmenu,
    toggleShowSubmenu,
    linkCount,
    ctaCount,
  } = useContext(Context);

  const { click } = useTrackingManager();
  const { breakpoint } = useBreakpoint();

  const navRef = useRef<HTMLDivElement | null>(null);
  const ariaSubmenu = useI18n('ariaSubmenu');
  const ariaScrollLeft = useI18n('ariaScrollLeft');
  const ariaScrollRight = useI18n('ariaScrollRight');

  const navItems: NavigationBarProps['items'] = links.map(
    ({ text, href, ariaLabel, opensInNewTab, currentPage }, i) => ({
      label: (
        <UeReference key={text} propertyPath={`links.${i}`} behavior="component">
          <UeElement type="text" label="Sub Pages">
            {text}
          </UeElement>
        </UeReference>
      ),
      href,
      target: opensInNewTab ? '_blank' : undefined,
      rel: opensInNewTab ? 'noopener noreferrer' : undefined,
      active: !!currentPage,
      icon: undefined,
      iconPosition: 'leading',
      onClick: () => {
        click('link', text, href, `${i + 1}`, breakpoint, '');
      },
      'aria-label': !currentPage && ariaLabel ? ariaLabel : undefined,
    }),
  );

  useEffect(() => {
    if (navRef.current) {
      attachHorizontalNavigation(navRef.current);
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (showSubmenu && window.innerWidth >= 1024) {
      toggleShowSubmenu();
    }
  }, [showSubmenu]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <DesktopMenuContainer isSticky={stickymode}>
      <DesktopMenuWrapper
        withCTA={hasCta}
        ref={submenuDesktopRef}
        className={showSubmenu && navItems.length > 0 ? 'show' : undefined}
        id={SUBMENUID.DESKTOP}
      >
        <UeContainer propertyPath="links" label="Sub Pages">
          <NavigationBar
            id="subMenuNavbar"
            scrollButtons={{
              left: {
                ariaLabel: ariaScrollLeft,
                onClick: () => click('arrow', 'Left', '', '', breakpoint, '', linkCount, ctaCount),
              },
              right: {
                ariaLabel: ariaScrollRight,
                onClick: () => click('arrow', 'Right', '', '', breakpoint, '', linkCount, ctaCount),
              },
            }}
            size="sm"
            items={navItems}
            className="subpage-navigation-bar"
            aria-label={ariaSubmenu}
            aria-labelledby={TRIGGERID.DESKTOP}
            ref={navRef}
          />
        </UeContainer>
      </DesktopMenuWrapper>
    </DesktopMenuContainer>
  );
};

export default DesktopMenu;
